import React from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import ModalWindow from '../../Modals/ModalWindow/ModalWindow';
import ModalButton from '../../Modals/ModalWindow/ModalButton/ModalButton';
import {tutorialCloseModal} from '../../../redux/actions';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {
  selectIsTutorialRepeatRun,
  selectTutorialActiveModalId,
  selectTutorialChapterId
} from '../../../redux/reducers/privateStateReducer/selectors';
import {sendTutorialEvent} from '../../../analytics/gameAnalytics';
import {
  getTutorialModalKey,
  getTutorialRunKind,
  TUTORIAL_ANALYTICS_EVENTS
} from '../../../utils/tutorial/tutorialAnalyticsIds';
import './TutorialModal.css';

// Модалки обучения идут цепочками (реплики Барбы, приветствие, первое
// задание), и кнопка следующей нередко оказывается на месте кнопки только что
// закрытой: второй клик двойного клика закрыл бы ее непрочитанной. Закрытие
// чаще этого интервала — повтор того же нажатия, а не решение игрока.
// Защита по часам, а не CSS-анимацией: анимации стоят, пока вкладка не
// рисует кадры, и кнопка осталась бы мертвой. Часы — монотонные
// (performance.now): системное время переводят назад (синхронизация, ручная
// правка), и кнопки молчали бы, пока оно не догонит прежнее
export const TUTORIAL_MODAL_REPEATED_CLOSE_MS = 400;

// Время последнего закрытия — на стор: в приложении он один, а у каждого
// теста свой, и защита одного теста не глотает клики другого
const lastCloseAtByStore = new WeakMap();

/**
 * Базовая обучающая модалка: заголовок + контент + кнопка «Понятно».
 * handleClose в ModalWindow НЕ передается намеренно — закрытие только кнопкой,
 * чтобы игрок не смахнул объяснение случайным кликом мимо.
 *
 * className — дополнительный класс окна; overlayClassName/overlayContent и
 * borderColor — см. ModalWindow (портрет и рамки у реплик Барбы).
 */
const TutorialModal = ({
  title,
  children,
  buttonText = 'Понятно',
  className = '',
  overlayClassName = '',
  overlayContent = null,
  borderColor,
}) => {
  const dispatch = useDispatch();
  const chapterId = useSelector(selectTutorialChapterId);
  const activeModalId = useSelector(selectTutorialActiveModalId);
  const isRepeatRun = useSelector(selectIsTutorialRepeatRun);
  const store = useStore();

  const handleClose = () => {
    const now = performance.now();
    const sinceLastCloseMs = now - (lastCloseAtByStore.get(store) ?? -Infinity);
    if (sinceLastCloseMs >= 0 && sinceLastCloseMs < TUTORIAL_MODAL_REPEATED_CLOSE_MS) {
      return;
    }
    lastCloseAtByStore.set(store, now);

    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    // Закрытие — единственная кнопка модалки: игрок дочитал объяснение
    const modalKey = getTutorialModalKey(chapterId, activeModalId);
    if (modalKey) {
      sendTutorialEvent(chapterId, TUTORIAL_ANALYTICS_EVENTS.MODAL_CLOSED, modalKey, getTutorialRunKind(isRepeatRun));
    }
    dispatch(tutorialCloseModal());
  };

  return (
    <ModalWindow
      className={`tutorial-modal ${className}`}
      overlayClassName={overlayClassName}
      overlayContent={overlayContent}
      borderColor={borderColor}
    >
      {title && <h2 className={'tutorial-modal_title'}>{title}</h2>}

      {/* key: у каждой модалки текст прокручен к началу — окна одного
          компонента (реплики Барбы) иначе унаследовали бы прокрутку
          предыдущего окна и открывались бы без первых строк */}
      <div key={activeModalId} className={'tutorial-modal_content'}>
        {children}
      </div>

      <div className={'tutorial-modal_footer'}>
        <ModalButton onClick={handleClose} text={buttonText}/>
      </div>
    </ModalWindow>
  );
};

export default TutorialModal;
